import { useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { getAdminGroupGuid, getGuidsToSkip, hasAdminPrivileges, isRolesEnabled, isSaaSCluster, } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ShareType } from "../../../../services/generated/graphql-types";
import { useGetRolesLazyQuery } from "../../../../services/role/role-service";
import { useAssignPinboardsToGroupMutation, useCreateGroupMutation, useGetDefaultLiveboardsLazyQuery, useGetGroupDetailsByIdLazyQuery, useGetUserGroupPrincipalListQuery, useGetUsersListPrincipalQuery, useResendGroupWelcomeEmailMutation, useSendWelcomeEmailMutation, useUpdateGroupMutation, } from "../../../../services/user-group/user-group-service";
import { getTranslatedInfoForMessageCode } from "../../../../utils/error-handling/error-strings-mapping";
import { getErrorObjectFromGraphQLErrors } from "../../../../utils/error-handling/error-utils";
import { CHECKBOX_SECTION_PAGINATION_BATCHSIZE, getErrorCode, MODE, } from '../../util';
import { defaultGroupPrivileges, getExposedGroupPrivileges, } from '../groups-privileges.util';
import { createEditTabLinkLabels, getGroupsForGroup, getUsersForGroup, GROUP_TABS, roleListParams, transformToIdsFromListObject, } from '../util';
import { CreateEditForm } from './create-edit-form.container';
export var FIELD;
(function (FIELD) {
    FIELD["NAME"] = "name";
    FIELD["DESCRIPTION"] = "description";
})(FIELD || (FIELD = {}));
export const CreateEditModal = ({ isModalOpened, closeModal, isEditMode = false, setIsRefresh = _.noop, setIsLoading, successBannerMode, groupId = '', }) => {
    const transientGroup = useRef(null);
    const isFilterTouched = useRef(false);
    const isDefaultLiveboardPatternUpdated = useRef(false);
    const allListData = useRef({
        [GROUP_TABS.ROLES]: [],
        [GROUP_TABS.GROUPS]: [],
        [GROUP_TABS.USERS]: [],
        [GROUP_TABS.LIVEBOARDS]: [],
    });
    const preSelectedListGroupsInGroupRef = useRef(null);
    const preSelectedListUsersInGroupRef = useRef(null);
    const preSelectedRolesRef = useRef(null);
    const preSelectedLiveboardsRef = useRef(null);
    const { t } = useTranslation();
    const [isEditReady, setIsEditReady] = useState(false);
    const [roleIds, setRoleIds] = useState([]);
    const [preSelectedRoleIds, setPreSelectedRoleIds] = useState([]);
    const [userIds, setUserIds] = useState([]);
    const [preSelectedUserIds, setPreSelectedUserIds] = useState([]);
    const [groupListIds, setGroupListIds] = useState([]);
    const [preSelectedGroupListIds, setPreSelectedGroupListIds] = useState([]);
    const [liveboardListIds, setLiveboardListIds] = useState([]);
    const [preSelectedLiveboardListIds, setPreSelectedLiveboardListIds,] = useState([]);
    const [defaultLiveboardList, setDefaultLiveboardList] = useState([]);
    const [roleList, setRoleList] = useState([]);
    const [userGroupList, setUserGroupList] = useState([]);
    const [userList, setUserList] = useState([]);
    const [filteredRoleList, setFilteredRoleList] = useState([]);
    const [filteredUserGroupList, setFilteredUserGroupList] = useState([]);
    const [filteredUserList, setFilteredUserList] = useState([]);
    const [filteredDefaultLiveboardList, setFilteredDefaultLiveboardList,] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [userListInput, setUserListInput] = useState({
        searchText: '',
        guidsToSkip: getGuidsToSkip(),
        batchSize: CHECKBOX_SECTION_PAGINATION_BATCHSIZE,
        sort: 'NAME',
        sortAscending: true,
    });
    const [groupListInput, setGroupListInput] = useState({
        searchText: '',
        guidsToSkip: getGuidsToSkip(),
        batchSize: CHECKBOX_SECTION_PAGINATION_BATCHSIZE,
    });
    const [defaultLiveboardInput, setDefaultLiveboardInput] = useState({
        offset: 0,
        pattern: '',
        minimumAccessLevel: ShareType.ReadOnly,
        batchSize: CHECKBOX_SECTION_PAGINATION_BATCHSIZE,
        principalId: groupId,
    });
    const [roleListInput, setRoleListInput] = useState({ ...roleListParams });
    const errorCB = () => {
        setErrorMessage(null);
    };
    const { data: rolesData, error: rolesError, loading: rolesLoading, getRoles, } = useGetRolesLazyQuery(roleListInput);
    const { data: userData, error: userError, loading: userLoading, } = useGetUsersListPrincipalQuery({
        pattern: userListInput.searchText,
        skipIds: userListInput.guidsToSkip,
        batchSize: userListInput.batchSize,
        sort: userListInput.sort,
        sortAscending: userListInput.sortAscending,
    });
    const { data: groupsData, error: groupsError, loading: groupsLoading, } = useGetUserGroupPrincipalListQuery(groupListInput.searchText, groupListInput.guidsToSkip, groupListInput.batchSize);
    const { data: groupByIdData, error: groupByIdError, loading: groupByIdLoading, getGroupById, } = useGetGroupDetailsByIdLazyQuery([groupId], groupId);
    const { data: createGroupData, error: createGroupError, loading: createGroupLoading, createGroup, } = useCreateGroupMutation();
    const { data: updateGroupData, error: updateGroupError, loading: updateGroupLoading, updateGroup, } = useUpdateGroupMutation();
    const { data: updateLiveboardData, error: updateLiveboardError, loading: updateLiveboardLoading, assignPinboards, } = useAssignPinboardsToGroupMutation();
    const { data: welcomeEmailData, error: welcomeEmailError, loading: welcomeEmailLoading, resendGroupWelcomeEmail: resendGroupWelcomeEmailFn, } = useResendGroupWelcomeEmailMutation();
    const { data: testWelcomeEmailData, error: testWelcomeEmailError, loading: testWelcomeEmailLoading, sendWelcomeEmail: testWelcomeEmailFn, } = useSendWelcomeEmailMutation();
    const { data: defaultLiveboardsData, error: defaultLiveboardsError, loading: defaultLiveboardsLoading, getDefaultLiveboards, } = useGetDefaultLiveboardsLazyQuery();
    useEffect(() => {
        if (isRolesEnabled()) {
            getRoles();
        }
        else {
            const privileges = getExposedGroupPrivileges();
            setRoleList(privileges);
            setFilteredRoleList(privileges);
            setRoleIds(privileges.map((privilege) => ({
                id: privilege.id,
            })));
            if (!isEditMode) {
                setPreSelectedRoleIds([...defaultGroupPrivileges]);
            }
        }
    }, []);
    useEffect(() => {
        if (isRolesEnabled()) {
            getRoles();
        }
    }, [roleListInput]);
    useEffect(() => {
        if (isEditMode) {
            getGroupById();
        }
    }, [isEditMode]);
    useEffect(() => {
        const isEnabledRoles = isRolesEnabled();
        if (isEditMode &&
            (rolesData || !isEnabledRoles) &&
            userData &&
            groupsData &&
            groupByIdData) {
            if (isEnabledRoles) {
                let roleData = rolesData?.Data?.roles || [];
                const preSelectedRoles = groupByIdData?.getGroupDetailsById[0].assignedRoles || [];
                let preSelectedRolesData = roleData.filter(item => preSelectedRoles.includes(item.id));
                if (!preSelectedRolesRef.current) {
                    preSelectedRolesRef.current = preSelectedRolesData;
                }
                else {
                    preSelectedRolesData = preSelectedRolesRef.current;
                }
                const sortedSelectedRoles = preSelectedRolesData.sort((a, b) => a.name.localeCompare(b.name));
                if (!roleListInput.pattern) {
                    roleData = [
                        ...sortedSelectedRoles,
                        ...roleData.filter(role => !preSelectedRoles.includes(role.id)),
                    ];
                }
                setRoleList(roleData);
                allListData.current[GROUP_TABS.ROLES] = [...roleData];
                setFilteredRoleList(roleData);
                setRoleIds(roleData.map(record => ({ id: record.id })));
                setPreSelectedRoleIds(groupByIdData?.getGroupDetailsById[0].assignedRoles);
            }
            else {
                setPreSelectedRoleIds(groupByIdData?.getGroupDetailsById[0].privileges || []);
            }
            const pGroupsData = groupsData?.objects.filter(item => groupByIdData?.getGroupDetailsById[0].id !== item.id) || [];
            let listGroupsInGroupById = getGroupsForGroup(groupByIdData?.listGroupsInGroupById) || [];
            if (!preSelectedListGroupsInGroupRef.current) {
                preSelectedListGroupsInGroupRef.current = listGroupsInGroupById;
            }
            else {
                listGroupsInGroupById = preSelectedListGroupsInGroupRef.current;
            }
            const selectedGroupIds = listGroupsInGroupById.map((item) => item.id);
            const sortedListGroupsInGroupById = listGroupsInGroupById.sort((a, b) => a.name.localeCompare(b.name));
            let groups = pGroupsData;
            if (!groupListInput.searchText) {
                groups = [
                    ...sortedListGroupsInGroupById,
                    ...pGroupsData.filter(group => !selectedGroupIds.includes(group.id)),
                ];
            }
            const pUsersData = userData?.objects || [];
            let listUsersInGroupById = getUsersForGroup(groupByIdData?.listUsersHeaderInGroupById || []) || [];
            if (!preSelectedListUsersInGroupRef.current) {
                preSelectedListUsersInGroupRef.current = listUsersInGroupById;
            }
            else {
                listUsersInGroupById = preSelectedListUsersInGroupRef.current;
            }
            const selectedUserIds = listUsersInGroupById.map((item) => item.id);
            const sortedListUsersInGroupById = listUsersInGroupById.sort((a, b) => a.name.localeCompare(b.name));
            let users = pUsersData;
            if (!userListInput.searchText) {
                users = [
                    ...sortedListUsersInGroupById,
                    ...pUsersData.filter(user => !selectedUserIds.includes(user.id)),
                ];
            }
            setUserGroupList(groups);
            allListData.current[GROUP_TABS.GROUPS] = [...groups];
            setFilteredUserGroupList(groups);
            setUserList(users);
            allListData.current[GROUP_TABS.USERS] = [...users];
            setFilteredUserList(users);
            setGroupListIds(groups.map(record => ({ id: record.id })));
            setUserIds(users.map(record => ({ id: record.id })));
            setPreSelectedUserIds(groupByIdData?.listUsersHeaderInGroupById.map(item => item.id));
            setPreSelectedGroupListIds(groupByIdData?.listGroupsInGroupById.map(item => item.header.id));
            if (!isSaaSCluster() &&
                !groupByIdData?.getGroupDetailsById[0].isSystemPrincipal) {
                setIsLoading(true);
                setPreSelectedLiveboardListIds(groupByIdData?.getGroupDetailsById[0].assignedPinboards);
                if (hasAdminPrivileges()) {
                    getDefaultLiveboards(defaultLiveboardInput);
                }
                else {
                    setIsEditReady(true);
                }
            }
            else {
                setIsEditReady(true);
                setIsLoading(false);
            }
        }
    }, [
        isEditMode,
        rolesData,
        rolesLoading,
        userData,
        groupsData,
        userLoading,
        groupsLoading,
        groupByIdData,
        groupByIdLoading,
    ]);
    useEffect(() => {
        if (rolesData) {
            let data = rolesData?.Data?.roles || [];
            const preSelectedRoles = preSelectedRolesRef.current || [];
            const preSelectedRolesIds = preSelectedRoles.map((item) => item.id);
            const sortedPreSelectedRoles = preSelectedRoles.sort((a, b) => a.name.localeCompare(b.name));
            if (!roleListInput.pattern) {
                data = [
                    ...sortedPreSelectedRoles,
                    ...data.filter(role => !preSelectedRolesIds.includes(role.id)),
                ];
            }
            let allRoles = [...data];
            if (roleListInput.pattern) {
                allRoles = _.uniqBy([...roleList, ...allRoles], 'id');
            }
            setRoleList(data);
            setFilteredRoleList(data);
            allListData.current[GROUP_TABS.ROLES] = allRoles;
            setRoleIds(data.map(record => ({ id: record.id })));
        }
    }, [rolesData, rolesLoading]);
    useEffect(() => {
        if (groupsData) {
            let groups = groupsData?.objects || [];
            const listGroupsInGroupById = preSelectedListGroupsInGroupRef.current || [];
            const pGroupsData = groupsData?.objects.filter(item => groupByIdData?.getGroupDetailsById[0].id !== item.id) || [];
            const selectedGroupIds = listGroupsInGroupById.map((item) => item.id);
            const sortedListGroupsInGroupById = listGroupsInGroupById.sort((a, b) => a.name.localeCompare(b.name));
            if (groupListInput.searchText) {
                groups = pGroupsData;
            }
            else {
                groups = [
                    ...sortedListGroupsInGroupById,
                    ...pGroupsData.filter(group => !selectedGroupIds.includes(group.id)),
                ];
            }
            let allGroups = [...groups];
            if (groupListInput.searchText) {
                allGroups = _.uniqBy([...userGroupList, ...allGroups], 'id');
            }
            setUserGroupList(groups);
            setFilteredUserGroupList(groups);
            allListData.current[GROUP_TABS.GROUPS] = allGroups;
            setGroupListIds(groups.map(record => ({ id: record.id })));
        }
    }, [groupsData, groupsLoading]);
    useEffect(() => {
        if (userData) {
            let users = userData?.objects || [];
            const listUsersInGroupById = preSelectedListUsersInGroupRef.current || [];
            const pUsersData = userData?.objects || [];
            const selectedUserIds = listUsersInGroupById.map((item) => item.id);
            const sortedListUsersInGroupById = listUsersInGroupById.sort((a, b) => a.name.localeCompare(b.name));
            if (!userListInput.searchText) {
                users = [
                    ...sortedListUsersInGroupById,
                    ...pUsersData.filter(user => !selectedUserIds.includes(user.id)),
                ];
            }
            let allUsers = [...users];
            if (userListInput.searchText) {
                allUsers = _.uniqBy([...userList, ...allUsers], 'id');
            }
            setUserList(users);
            setFilteredUserList(users);
            allListData.current[GROUP_TABS.USERS] = allUsers;
            setUserIds(users.map(record => ({ id: record.id })));
        }
    }, [userData, userLoading]);
    useEffect(() => {
        const isFormDataReady = isEditMode
            ? isEditReady
            : Boolean(userData && groupsData);
        if (isFormDataReady) {
            setIsLoading(false);
        }
    }, [isEditMode, isEditReady, userData, groupsData]);
    useEffect(() => {
        if (defaultLiveboardsData) {
            const defaultLiveboards = defaultLiveboardsData?.objects || [];
            let liveboards = _.sortBy(defaultLiveboards, ['name']);
            let selectedLiveboardIds = groupByIdData?.getGroupDetailsById[0].assignedPinboards || [];
            if (selectedLiveboardIds?.length > 0 &&
                preSelectedLiveboardsRef.current) {
                const newlySelected = preSelectedLiveboardsRef.current.filter((data) => !selectedLiveboardIds.includes(data.id));
                selectedLiveboardIds = [
                    ...selectedLiveboardIds,
                    ...newlySelected.map((data) => data.id),
                ];
            }
            let selectedLiveboardData = liveboards.filter((item) => selectedLiveboardIds.includes(item.id));
            if (!preSelectedLiveboardsRef.current) {
                preSelectedLiveboardsRef.current = selectedLiveboardData;
            }
            else {
                selectedLiveboardData = preSelectedLiveboardsRef.current;
            }
            const sortedSelectedLiveboardData = selectedLiveboardData.sort((a, b) => a.name.localeCompare(b.name));
            if (!defaultLiveboardInput.pattern) {
                liveboards = [
                    ...sortedSelectedLiveboardData,
                    ...liveboards.filter((liveboard) => !selectedLiveboardIds.includes(liveboard.id)),
                ];
            }
            let allLiveboards = [...liveboards];
            if (defaultLiveboardInput.pattern) {
                allLiveboards = _.uniqBy([...liveboards, ...allLiveboards], 'id');
            }
            setDefaultLiveboardList(liveboards);
            setFilteredDefaultLiveboardList(liveboards);
            allListData.current[GROUP_TABS.LIVEBOARDS] = allLiveboards;
            setLiveboardListIds(liveboards.map((record) => ({ id: record.id })));
            setIsEditReady(true);
            setIsLoading(false);
        }
    }, [defaultLiveboardsData, defaultLiveboardsLoading]);
    useEffect(() => {
        if (defaultLiveboardsError) {
            setIsLoading(false);
        }
    }, [defaultLiveboardsError, defaultLiveboardsLoading]);
    useEffect(() => {
        if (rolesError) {
            setIsLoading(false);
        }
    }, [rolesError, rolesLoading]);
    useEffect(() => {
        if (groupByIdError) {
            setIsLoading(false);
        }
    }, [groupByIdError, groupByIdLoading]);
    useEffect(() => {
        if (userError || groupsError) {
            setIsLoading(false);
        }
    }, [userError, groupsError, userLoading, groupsError]);
    useEffect(() => {
        if (createGroupData) {
            successBannerMode(MODE.ADD);
            const createGroupAction = new UserAction(userActionTypeEnum.CREATE_GROUP);
            const properties = {
                groupId: createGroupData.id,
                visibility: createGroupData.visibility,
                privileges: isRolesEnabled()
                    ? createGroupData.granularPrivileges
                    : createGroupData.privileges,
                userCount: transientGroup.current?.users.length || 0,
                groupCount: createGroupData.assignedGroups.length,
            };
            collectEvent(createGroupAction, properties);
            transientGroup.current = null;
            closeModal();
        }
    }, [createGroupData, createGroupLoading]);
    useEffect(() => {
        if (updateGroupData) {
            successBannerMode(MODE.UPDATE);
            const updateGroupAction = new UserAction(userActionTypeEnum.UPDATE_GROUP);
            const { groupid, users, groups, content } = transientGroup.current;
            const properties = {
                groupId: groupid,
                visibility: content.visibility,
                privileges: content.privileges,
                userCount: users.length,
                groupCount: groups.length,
                defaultPinboardCount: content.pinboardIds?.length || 0,
            };
            collectEvent(updateGroupAction, properties);
            transientGroup.current = null;
            closeModal();
        }
    }, [updateGroupData, updateGroupLoading]);
    useEffect(() => {
        if (!isSaaSCluster() && isDefaultLiveboardPatternUpdated.current) {
            setIsLoading(true);
            getDefaultLiveboards(defaultLiveboardInput);
        }
    }, [defaultLiveboardInput]);
    const filterListData = (tabs, data = []) => {
        const isSaas = isSaaSCluster();
        switch (tabs) {
            case createEditTabLinkLabels(isRolesEnabled(), isEditMode)[1]:
                setFilteredRoleList(data);
                break;
            case createEditTabLinkLabels(isRolesEnabled(), isEditMode)[isSaas ? 2 : 3]:
                setFilteredUserGroupList(data);
                break;
            case createEditTabLinkLabels(isRolesEnabled(), isEditMode)[isSaas ? 3 : 4]:
                setFilteredUserList(data);
                break;
            default:
                break;
        }
    };
    const onSendWelcomeEmail = async (id, message, resend) => {
        await resendGroupWelcomeEmailFn(id, message, resend);
    };
    const onSendTestWelcomeEmail = async (id, message) => {
        await testWelcomeEmailFn(id, message);
    };
    const onSuccess = async (newData) => {
        setIsLoading(true);
        transientGroup.current = newData;
        const defaultAdminGroupGuid = getAdminGroupGuid();
        const sanitizedData = { ...transientGroup.current };
        if (sanitizedData.groupid === defaultAdminGroupGuid) {
            sanitizedData.shouldNotUpdateGroup = true;
        }
        transientGroup.current = sanitizedData;
        try {
            if (isEditMode) {
                if (!isSaaSCluster() &&
                    !groupByIdData?.getGroupDetailsById[0].isSystemPrincipal) {
                    const { liveboards, ...restData } = sanitizedData;
                    await updateGroup(restData);
                    assignPinboards(liveboards, restData.groupid);
                }
                else {
                    await updateGroup(sanitizedData);
                }
            }
            else {
                await createGroup(sanitizedData);
            }
            setIsLoading(false);
        }
        catch (err) {
            const errorCode = getErrorCode(getErrorObjectFromGraphQLErrors(err));
            const errorText = t(getTranslatedInfoForMessageCode(errorCode).summary);
            setErrorMessage(errorText);
            setIsLoading(false);
        }
    };
    const updateUserListInput = useCallback((searchText) => {
        isFilterTouched.current = true;
        setUserListInput({
            ...userListInput,
            searchText,
        });
    }, [userListInput]);
    const updateDefaultLiveboardListInput = useCallback((searchText) => {
        isFilterTouched.current = true;
        isDefaultLiveboardPatternUpdated.current = true;
        setDefaultLiveboardInput({
            ...defaultLiveboardInput,
            pattern: searchText ? `%${searchText}%` : '',
        });
    }, [defaultLiveboardInput]);
    const updateUserGroupsListInput = useCallback((searchText) => {
        isFilterTouched.current = true;
        setGroupListInput({
            ...groupListInput,
            searchText,
        });
    }, [groupListInput]);
    const updateRoleListInput = useCallback((pattern) => {
        isFilterTouched.current = true;
        setRoleListInput({
            ...roleListInput,
            pattern,
        });
    }, [roleListInput]);
    const updatePreDisabledLiveboardIds = () => {
        if (!isEditMode ||
            isSaaSCluster() ||
            groupByIdData?.getGroupDetailsById[0].isSystemPrincipal ||
            preSelectedLiveboardListIds.length < 3) {
            return [];
        }
        return transformToIdsFromListObject(liveboardListIds, preSelectedLiveboardListIds);
    };
    return (React.createElement(React.Fragment, null, (isFilterTouched.current ||
        (!isEditMode && userData && groupsData) ||
        (isEditMode && isEditReady)) && (React.createElement(CreateEditForm, { groupData: groupByIdData, message: errorMessage, isEditMode: isEditMode, isModalOpened: isModalOpened, setShowError: errorCB, closeModal: closeModal, onSuccess: onSuccess, roleIds: roleIds, userIds: userIds, groupListIds: groupListIds, preSelectedRoleIds: preSelectedRoleIds, preSelectedUserIds: preSelectedUserIds, preSelectedGroupListIds: preSelectedGroupListIds, roleList: roleList, userGroupList: userGroupList, userList: userList, filteredRoleList: filteredRoleList, filteredUserGroupList: filteredUserGroupList, filteredUserList: filteredUserList, filterListData: filterListData, onSendWelcomeEmail: onSendWelcomeEmail, onSendTestWelcomeEmail: onSendTestWelcomeEmail, setUserListInput: updateUserListInput, setUserGroupsListInput: updateUserGroupsListInput, setRoleListInput: updateRoleListInput, setDefaultLiveboardInput: updateDefaultLiveboardListInput, liveboardListIds: liveboardListIds, defaultLiveboardList: defaultLiveboardList, filteredDefaultLiveboardList: filteredDefaultLiveboardList, preSelectedLiveboardListIds: preSelectedLiveboardListIds, preDisabledLiveboardListIds: updatePreDisabledLiveboardIds(), isUserListLoading: userLoading, isGroupListLoading: groupsLoading, isRoleListLoading: rolesLoading, isLiveboardListLoading: defaultLiveboardsLoading, allListData: allListData, preSelectedListGroupsInGroupRef: preSelectedListGroupsInGroupRef, preSelectedListUsersInGroupRef: preSelectedListUsersInGroupRef, preSelectedRolesRef: preSelectedRolesRef, preSelectedLiveboardsRef: preSelectedLiveboardsRef }))));
};
//# sourceMappingURL=create-edit-modal.container.js.map